import React, { useState, FormEvent } from 'react';
import { loginWithOnboardingToken } from '../libraries/auth';
import logo from '../images/planyvo.png';

interface OnboardingLoginPageProps {
  onLoginSuccess: (user: any, token: string) => Promise<void>;
}

const OnboardingLoginPage: React.FC<OnboardingLoginPageProps> = ({ onLoginSuccess }) => {
  const [onboardingCode, setOnboardingCode] = useState('');
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiUrl = import.meta.env.VITE_API_URL || '';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!onboardingCode.trim()) {
      setError('Please enter your onboarding code');
      return;
    }
    if (!acceptedLegal) {
      setError('Bitte akzeptieren Sie die Nutzungsbedingungen und Datenschutzerklärung.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await loginWithOnboardingToken(onboardingCode.trim(), apiUrl);
      await onLoginSuccess(response.user, response.token);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An error occurred during login. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 bg-white"
    >
      <div className="max-w-md w-full border-1 border-gray-200 rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <img src={logo} alt="Planyvo Logo" className="mx-auto mb-6 h-30" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Willkommen</h1>
          <p className="text-gray-600">Gebe dein onboarding Code ein um Zugang zu deiner EventApp zu erhalten.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
          <div>
            <label htmlFor="onboardingCode" className="block text-sm font-medium text-gray-700 mb-2">
              Onboarding Code
            </label>
            <input
              id="onboardingCode"
              name="onboardingCode"
              type="text"
              value={onboardingCode}
              onChange={(e) => setOnboardingCode(e.target.value)}
              placeholder="Code eingeben"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition"
              disabled={loading}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              autoFocus
            />
          </div>

          <label className="flex items-start gap-3 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={acceptedLegal}
              onChange={(e) => setAcceptedLegal(e.target.checked)}
              disabled={loading}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span>
              ich akzeptiere die{' '}
              <a href="https://planyvo.com/nutzungsbedingungen" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                Nutzungsbedingungen
              </a>{' '}
              und habe die{' '}
              <a href="https://planyvo.com/datenschutz" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                Datenschutzbestimmungen
              </a>{' '}
              zur Kenntnis genommen.
            </span>
          </label>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !acceptedLegal}
            className="w-full bg-primary hover:bg-primary/90 disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                <span className="ml-2">anmelden...</span>
              </>
            ) : (
              'beitreten'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Du hast kein Onboarding Code? Schreibe uns an hello@planyvo.com
          </p>
        </div>
      </div>
    </div>
  );
};

export default OnboardingLoginPage;
